import {
  autoUpdate,
  flip,
  FloatingPortal,
  hide,
  offset,
  shift,
  size,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import { interactionHistoryId, InteractionHistoryItem } from '@poseidon/components/ContactDetail';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

interface Props {
  /** The step's entries, oldest first: one entry, or a whole conversation. */
  entries: HistoryItem[];
  /** A conversation's title, above its thread (a single entry says what it is itself). */
  title?: string;
  actions: InteractionHistoryActions;
  /** The step it opened from — the popover tracks it as the canvas pans / zooms. */
  anchor: Element | null;
  onClose: () => void;
}

/** The tallest the entry may grow before it scrolls inside itself (screen px). */
const MAX_HEIGHT_PX = 520;

/**
 * A history step opened on the sequence canvas: the FULL entry — the whole message, the transcript, Respond —
 * at natural size, or a conversation step's whole thread as the feed draws it (theirs left, ours right), opened
 * on its newest message like a chat. Portaled out of the canvas, so it's never scaled by the zoom, clipped by
 * the viewport, or painted under a node; and it scrolls INSIDE itself (overscroll contained), so a long reply
 * scrolls the reply — never the canvas behind it. Escape (shared esc stack, above the contact panel's own
 * handler) or a click elsewhere closes it.
 */
export function SequenceHistoryPopover({ entries, title, actions, anchor, onClose }: Props) {
  const float = useFloating({
    open: true,
    // Below the step (above it near the bottom): a narrow panel has no room BESIDE it.
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: [
      offset(8),
      flip({ padding: 12 }),
      shift({ padding: 12, crossAxis: true }),
      // Never taller than the room on screen — past that the entry scrolls inside the popover.
      size({
        padding: 12,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(160, Math.min(MAX_HEIGHT_PX, availableHeight))}px`;
        },
      }),
      // Panned so its step left the canvas → hide with it, rather than pinning to the screen edge.
      hide({ strategy: 'referenceHidden' }),
    ],
    elements: { reference: anchor },
  });

  // A thread opens on its newest message, its top just under the pinned header. Once placed, not before: the
  // `size` middleware caps the height during placement, and only past that cap is there anything to scroll.
  const popoverRef = useRef<HTMLDivElement>(null);
  const ref = useMergeRefs([float.refs.setFloating, popoverRef]);
  const { isPositioned } = float;
  const threadLength = entries.length;
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!isPositioned || el == null || threadLength < 2) return;
    const newest = el.querySelector<HTMLElement>('[data-history-entry]:last-child');
    const header = el.querySelector<HTMLElement>('[data-popover-header]');
    if (newest != null) el.scrollTop = newest.offsetTop - (header?.offsetHeight ?? 0);
  }, [isPositioned, threadLength]);

  useEscStack('SEQUENCE_HISTORY_POPOVER', ESC_PRIORITY.INTERSTITIAL, onClose);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element)) return;
      // Inside the popover, on another step (it opens that one instead), or in a menu / tooltip the entry
      // floats out of itself — all part of using it, not a click away.
      if (
        e.target.closest(
          '[data-history-popover], [data-history-step], [data-floating-ui-portal], .tippy-box, [popover]',
        ) != null
      )
        return;
      onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  return (
    <FloatingPortal>
      <div
        ref={ref}
        data-history-popover
        style={{
          ...float.floatingStyles,
          visibility: float.middlewareData.hide?.referenceHidden === true ? 'hidden' : 'visible',
        }}
        className="z-50 flex w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-y-auto overscroll-contain rounded-xl border border-lynch-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        {/* Pinned, so a thread opened on its newest message still shows what it is and how to close it. */}
        <div
          data-popover-header
          className="sticky -top-3 z-10 -mx-3 -mt-3 mb-1 flex items-center justify-end gap-2 bg-white px-3 pb-1 pt-2 dark:bg-slate-900">
          {title != null && (
            <span className="mr-auto truncate text-xs font-medium text-slate-700 dark:text-slate-200">{title}</span>
          )}
          <Button size="2xs" text icon={<General.XMark />} aria-label="Close" tooltip="Close" onClick={onClose} />
        </div>
        {entries.map((entry, i) => (
          // Its own column, so each entry still aligns theirs-left / ours-right like the feed.
          <div key={interactionHistoryId(entry)} data-history-entry className="flex flex-col">
            <InteractionHistoryItem
              entry={entry}
              actions={actions}
              prevDate={entries[i - 1]?.interaction.createdAt}
              isLast={i === entries.length - 1}
            />
          </div>
        ))}
      </div>
    </FloatingPortal>
  );
}
